import { ProjectService } from '@/api/project';
import { TestRunService } from '@/api/testRun';
import { getProjectOverview, downloadReportExport } from '@/api/report';
import { Loading } from '@/components/Loading';
import { ViewAssignee } from '@/components/project/ViewAssignee';
import { Section } from '@/components/Section';
import { ExportButton } from '@/components/shared/button/ExportButton';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { StatusColor } from '@/utils/constants/testRun';

interface StatusMetric {
  total: number;
  percentage: number;
}

interface ReportData {
  passed_percentage: number;
  total_test_case: number;
  status: {
    passed?: StatusMetric;
    untested?: StatusMetric;
    blocked?: StatusMetric;
    retest?: StatusMetric;
    failed?: StatusMetric;
    not_applicable?: StatusMetric;
    in_progress?: StatusMetric;
    hold?: StatusMetric;
    [key: string]: any;
  };
  charts: {
    defect_per_module: Array<{ module_name: string; total: number }>;
    defect_per_severity: Array<{ severity: string; total: number }>;
    execution_progress?: Array<{ date: string; total: number }>;
  };
}

const getSeverityBadgeClass = (severity: string) => {
  switch (severity.toUpperCase()) {
  case 'CRITICAL':
    return 'bg-red-50 text-red-700 border-red-200';
  case 'MAJOR':
    return 'bg-orange-50 text-orange-700 border-orange-200';
  case 'MINOR':
    return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  case 'TRIVIAL':
    return 'bg-slate-50 text-slate-700 border-slate-200';
  default:
    return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

export const OverviewProject: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // State untuk filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [testRunId, setTestRunId] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Date range validation
  const isDateRangeInvalid = Boolean(
    startDate && endDate && startDate > endDate,
  );

  // Fetch Project Data
  const { data: projectData, isPending: isProjectPending } =
    ProjectService.useGet(id);

  // Fetch daftar test run project untuk dropdown filter
  const { data: testRunOptions } = TestRunService.useGetList(id);

  // Fetch Report Data menggunakan React Query
  const { data: reportData, isPending: isReportPending } = useQuery<ReportData>(
    {
      queryKey: ['projectOverview', id, testRunId, startDate, endDate],
      queryFn: () =>
        getProjectOverview(id!, {
          test_run_id: testRunId,
          start_date: startDate,
          end_date: endDate,
        }),
      // Saat filter berubah (queryKey baru), data lama dipakai sebagai placeholder
      // (placeholderData) sehingga status = 'success' dan isPending tetap false
      // Hasilnya: halaman TIDAK di-unmount, popup DatePicker tidak tertutup paksa.
      placeholderData: keepPreviousData,
      enabled: !!id && !isDateRangeInvalid,
    },
  );

  const severityRows = useMemo(() => {
    const standardSeverities = ['CRITICAL', 'MAJOR', 'MINOR', 'TRIVIAL'];
    const defectList = reportData?.charts?.defect_per_severity || [];
    const itemMap = new Map(
      defectList.map((item: any) => [
        String(item.severity ?? '').toUpperCase(),
        item,
      ]),
    );

    const standards = standardSeverities.map(sev => {
      const found = itemMap.get(sev);
      return {
        severity: sev,
        total: Number(found?.total ?? found?.count ?? 0),
      };
    });

    const nonStandards = defectList.filter(
      (item: any) =>
        !standardSeverities.includes(String(item.severity ?? '').toUpperCase()),
    );

    return [...standards, ...nonStandards];
  }, [reportData?.charts?.defect_per_severity]);

  // PENTING: gate loading hanya boleh memakai isPending (belum ada data sama
  // sekali / first load), BUKAN isFetching. isFetching true pada setiap
  // background refetch; mem-unmount seluruh halaman saat itu membuat children
  // refetch ulang begitu mount kembali (staleTime default 0) sehingga
  // terjadi loop fetch + layar berkedip tiap kali filter berubah.
  if (isProjectPending || isReportPending) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  if (!projectData) {
    return <UnauthorizedPage />;
  }

  const selectedTestRun = (testRunOptions ?? []).find(r => r.id === testRunId);

  return (
    <Section
      title={`Overview: ${projectData?.title}`}
      actions={
        <div className="flex gap-2 items-center">
          <ExportButton
            label="Export Excel"
            loading={isExporting}
            disabled={isDateRangeInvalid}
            onClick={() => {
              if (isDateRangeInvalid) return;
              setIsExporting(true);
              downloadReportExport(id!, {
                test_run_id: testRunId,
                start_date: startDate,
                end_date: endDate,
              })
                .catch(() => alert('Gagal mendownload laporan!'))
                .finally(() => setIsExporting(false));
            }}
          />
          <ViewAssignee project={projectData!} />
        </div>
      }
    >
      {/* FILTER AREA */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col gap-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Start Date
            </label>
            <input
              type="date"
              className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                isDateRangeInvalid
                  ? 'border-red-500 bg-red-50/20'
                  : 'border-gray-300'
              }`}
              onChange={e => setStartDate(e.target.value)}
              value={startDate}
              max={endDate || undefined}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              End Date
            </label>
            <input
              type="date"
              className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                isDateRangeInvalid
                  ? 'border-red-500 bg-red-50/20'
                  : 'border-gray-300'
              }`}
              onChange={e => setEndDate(e.target.value)}
              value={endDate}
              min={startDate || undefined}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Test Run
            </label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              value={testRunId}
              onChange={e => setTestRunId(e.target.value)}
            >
              <option value="">Semua Test Run</option>
              {(testRunOptions ?? []).map(testRun => (
                <option key={testRun.id} value={testRun.id}>
                  {testRun.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isDateRangeInvalid && (
          <p className="text-xs text-red-600 font-medium">
            Start date cannot be greater than end date
          </p>
        )}

        {/* ACTIVE FILTER PILLS */}
        {(testRunId || startDate || endDate) && (
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 text-xs">
            <span className="text-gray-400 font-medium">Filter Aktif:</span>
            {selectedTestRun && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                Test Run: {selectedTestRun.title}
                <button
                  type="button"
                  onClick={() => setTestRunId('')}
                  className="hover:text-blue-900 ml-0.5 text-base leading-none font-bold"
                  aria-label="Hapus filter test run"
                >
                  &times;
                </button>
              </span>
            )}
            {(startDate || endDate) && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">
                Periode: {startDate || 'Awal'} s/d {endDate || 'Akhir'}
                <button
                  type="button"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="hover:text-indigo-900 ml-0.5 text-base leading-none font-bold"
                  aria-label="Hapus filter periode"
                >
                  &times;
                </button>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                setTestRunId('');
                setStartDate('');
                setEndDate('');
              }}
              className="text-gray-500 hover:text-red-600 underline text-xs ml-auto font-medium"
            >
              Reset Filter
            </button>
          </div>
        )}
      </div>

      {/* STATUS BREAKDOWN CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{ borderTop: '4px solid #3B82F6' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: '#3B82F6' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.total_test_case ?? 0}
            </span>
            <span className="text-[11px] font-medium text-blue-600">
              {reportData?.passed_percentage ?? 0}% Pass Rate
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{ borderTop: `4px solid ${StatusColor.passed ?? '#8CC63F'}` }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Passed
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: StatusColor.passed ?? '#8CC63F' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.passed?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.passed?.percentage ?? 0}%
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{ borderTop: `4px solid ${StatusColor.failed ?? '#ED1C24'}` }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Failed
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: StatusColor.failed ?? '#ED1C24' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.failed?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.failed?.percentage ?? 0}%
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{ borderTop: `4px solid ${StatusColor.blocked ?? '#92278F'}` }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Blocked
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: StatusColor.blocked ?? '#92278F' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.blocked?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.blocked?.percentage ?? 0}%
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{
            borderTop: `4px solid ${StatusColor.untested ?? '#00A99D'}`,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Untested
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: StatusColor.untested ?? '#00A99D' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.untested?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.untested?.percentage ?? 0}%
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{ borderTop: `4px solid ${StatusColor.retest ?? '#2E3192'}` }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Retest
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: StatusColor.retest ?? '#2E3192' }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.retest?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.retest?.percentage ?? 0}%
            </span>
          </div>
        </div>

        <div
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
          style={{
            borderTop: `4px solid ${StatusColor.not_applicable ?? '#964B00'}`,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Not Applicable
            </span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{
                backgroundColor: StatusColor.not_applicable ?? '#964B00',
              }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-bold text-gray-800">
              {reportData?.status?.not_applicable?.total ?? 0}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {reportData?.status?.not_applicable?.percentage ?? 0}%
            </span>
          </div>
        </div>

        {(reportData?.status?.in_progress?.total ?? 0) > 0 && (
          <div
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
            style={{
              borderTop: `4px solid ${StatusColor.in_progress ?? '#F7931E'}`,
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                In Progress
              </span>
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: StatusColor.in_progress ?? '#F7931E',
                }}
              />
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-bold text-gray-800">
                {reportData?.status?.in_progress?.total ?? 0}
              </span>
              <span className="text-xs font-medium text-gray-400">
                {reportData?.status?.in_progress?.percentage ?? 0}%
              </span>
            </div>
          </div>
        )}

        {(reportData?.status?.hold?.total ?? 0) > 0 && (
          <div
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
            style={{ borderTop: `4px solid ${StatusColor.hold ?? '#CCCCCC'}` }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Hold
              </span>
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: StatusColor.hold ?? '#CCCCCC' }}
              />
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-bold text-gray-800">
                {reportData?.status?.hold?.total ?? 0}
              </span>
              <span className="text-xs font-medium text-gray-400">
                {reportData?.status?.hold?.percentage ?? 0}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* DEFECT ANALYSIS AREA */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <h3 className="text-gray-800 font-semibold text-sm">
              Defect per Epic / User Story
            </h3>
            <span className="text-xs px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
              {(reportData?.charts?.defect_per_module ?? []).reduce(
                (acc: number, curr: any) => acc + (curr.total ?? 0),
                0,
              )}{' '}
              Defect
            </span>
          </div>
          <div className="overflow-x-auto max-h-[350px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0">
                <tr>
                  <th className="py-2.5 px-4 text-left font-semibold">
                    Epic / User Story
                  </th>
                  <th className="py-2.5 px-4 text-right font-semibold">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(reportData?.charts?.defect_per_module ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={2} className="py-8 text-center text-gray-400">
                      Tidak ada defect tercatat
                    </td>
                  </tr>
                ) : (
                  (reportData?.charts?.defect_per_module ?? []).map(
                    (row, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-2.5 px-4 text-gray-700 font-medium">
                          {row.module_name ?? 'Tanpa Epic / User Story'}
                        </td>
                        <td className="py-2.5 px-4 text-right font-semibold text-gray-800">
                          {row.total ?? 0}
                        </td>
                      </tr>
                    ),
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <h3 className="text-gray-800 font-semibold text-sm">
              Defect by Severity
            </h3>
            <span className="text-xs px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
              {severityRows.reduce(
                (acc: number, curr: any) => acc + (curr.total ?? 0),
                0,
              )}{' '}
              Defect
            </span>
          </div>
          <div className="overflow-x-auto max-h-[350px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0">
                <tr>
                  <th className="py-2.5 px-4 text-left font-semibold">
                    Severity
                  </th>
                  <th className="py-2.5 px-4 text-right font-semibold">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {severityRows.map((row, index) => {
                  const severity = (row.severity ?? '-').toUpperCase();
                  const badgeClass = getSeverityBadgeClass(severity);

                  return (
                    <tr
                      key={index}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="py-2.5 px-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-md border ${badgeClass}`}
                        >
                          {severity}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right font-semibold text-gray-800">
                        {row.total ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Section>
  );
};
